/**
 * DashFlow onboarding configuration.
 *
 * Aesthetic: editorial paper-cream + terracotta, matching the super-admin
 * Payments panel (src/pages/superadmin/Payments.tsx). Fraunces serif +
 * Plus Jakarta Sans + JetBrains Mono — same fonts already loaded there.
 *
 * The structural pattern (5 steps, progress bar, split 50/50) is shared
 * across the 6 SaaS. See onboarding-spec/DESIGN_SPEC.md.
 */

import type {
  OnboardingConfig,
  ThemePreset,
} from '../components/onboarding/OnboardingWizard';

/* ----------------------- Unsplash story images ----------------------- */
// Fetched at runtime via Unsplash's CDN — avoids committing large binaries.
const unsplash = (id: string) => `https://images.unsplash.com/photo-${id}?w=1200&q=80`;

const STEP_IMAGES = {
  1: {
    url: unsplash('1553413077-190dd305871c'),
    alt: 'Estantes de un e-commerce con productos listos para despachar',
  },
  2: {
    url: unsplash('1586528116311-ad8dd3c8310d'),
    alt: 'Depósito con cajas etiquetadas — fotografía editorial',
  },
  3: {
    url: unsplash('1607082349566-187342175e2f'),
    alt: 'Pequeño comercio con vidriera iluminada — duotono cálido',
  },
  4: {
    url: unsplash('1607083206869-4c7672e72a8a'),
    alt: 'Escritorio de trabajo con productos, cuaderno y cuenta de ventas',
  },
  5: {
    url: unsplash('1506630448388-4e683c67ddb0'),
    alt: 'Productos listos, caja abierta, momento de lanzamiento',
  },
} as const;

const BACKGROUND_URL = unsplash('1441986300917-64674bd600d8');

/* --------------------------- Palette + type ------------------------- */
// Mirrors the super-admin Payments palette — INK, PAPER, ACCENT (terracota).
const palette = {
  paper: '#f4efe6',
  paperRule: 'rgba(26, 22, 18, 0.14)',
  ink: '#1a1612',
  inkSoft: '#4a4540',
  inkFaint: '#8a847d',
  primary: '#c13a16', // terracota editorial (coincide con SuperAdminPayments)
  primaryInk: '#f4efe6',
  secondary: '#244a53', // teal de la editorial
  overlay: 'rgba(26, 22, 18, 0.45)',
};

const typography = {
  display: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  mono: '"JetBrains Mono", ui-monospace, monospace',
};

const themePresets: ThemePreset[] = [
  {
    code: 'vibrant',
    label: 'Editorial — paper & terracota',
    primary: '#c13a16',
    secondary: '#244a53',
  },
  {
    code: 'inspiring',
    label: 'Inspiring — indigo comercial',
    primary: '#3c4a78',
    secondary: '#d4c5a0',
  },
  {
    code: 'light',
    label: 'Light — minimalista editorial',
    primary: '#222222',
    secondary: '#f5f5f5',
  },
  {
    code: 'dark',
    label: 'Dark — modo oscuro',
    primary: '#f5f5f5',
    secondary: '#2a2a2a',
    darkMode: true,
  },
  {
    code: 'custom',
    label: 'Crear mi propia paleta',
    primary: '#c13a16',
    secondary: '#244a53',
  },
];

/* -------------------- Fonts pre-load (runs on import) --------------- */
// Mirrors the strategy used in SuperAdminPayments so the wizard doesn't flash
// unstyled serif on mount.
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap';

export function ensureOnboardingFontsLoaded(): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  document.head.appendChild(link);
}

/* --------------------------- Full config ---------------------------- */

export function buildDashflowOnboardingConfig(prefill?: {
  name?: string;
  avatarUrl?: string;
  email?: string;
}): OnboardingConfig {
  return {
    productKey: 'dashflow',
    productName: 'DashFlow',
    // The wordmark lives in /src/logo.svg (served at root) — CRA inlines it.
    logoUrl: '/logo192.png',
    backgroundUrl: BACKGROUND_URL,
    stepImages: STEP_IMAGES,
    palette,
    typography,
    copy: {
      businessWord: 'tienda',
      step1Kicker: 'PASO 01 · 05',
      step1Title: 'Lo primero, saludarnos',
      step1Subtitle: '¿Cómo te llamás?',
      step2Kicker: 'PASO 02 · 05',
      step2Title: 'Contanos de tu tienda',
      step2Subtitle: 'Así te armamos la sincronización que te corresponde',
      step3Kicker: 'PASO 03 · 05',
      step3Title: 'Personalizá tu marca',
      step3Subtitle: 'Así la ven tus clientes. Se puede cambiar cuando quieras.',
      step4Kicker: 'PASO 04 · 05',
      step4Title: 'Queremos tener un detalle',
      step4Subtitle:
        'Te mandamos un regalo en tu cumple — y si querés, te hacemos un poco de prensa en redes.',
      step5Kicker: 'PASO 05 · 05',
      step5TitleTemplate: 'Listo, {name}',
      step5Subtitle: 'Tu DashFlow está armado. Entrá y cargá tu primer catálogo.',
      step5Cta: 'Ir al panel',
    },
    // Qualification per DESIGN_SPEC section 8.4 — no whoAreYou toggle.
    qualification: {
      activity: {
        key: 'activity',
        label: '¿Qué vendés?',
        placeholder: 'Elegí un rubro',
        options: [
          { value: 'indumentaria', label: 'Indumentaria' },
          { value: 'accesorios', label: 'Accesorios' },
          { value: 'comida', label: 'Comida / Alimentos' },
          { value: 'libreria', label: 'Librería / Regalería' },
          { value: 'electro', label: 'Electro' },
          { value: 'importador_mayorista', label: 'Importador mayorista' },
          { value: 'marca_propia', label: 'Marca propia (fábrica)' },
          { value: 'distribuidora', label: 'Distribuidora' },
          { value: 'otro', label: 'Otro' },
        ],
      },
      volume: {
        key: 'volume',
        label: '¿Cuántos SKUs manejás?',
        placeholder: 'Elegí un rango',
        options: [
          { value: 'lt_100', label: 'Menos de 100' },
          { value: '100_500', label: '100 a 500' },
          { value: '500_2000', label: '500 a 2.000' },
          { value: 'gt_2000', label: 'Más de 2.000' },
          { value: 'none', label: 'Aún no tengo catálogo' },
        ],
      },
      workMode: {
        key: 'workMode',
        label: '¿En qué canales vendés hoy?',
        placeholder: 'Elegí cómo vendés',
        options: [
          { value: 'solo_ml', label: 'Solo Mercado Libre' },
          { value: 'solo_tn', label: 'Solo Tienda Nube' },
          { value: 'ml_tn_sin_sync', label: 'ML + TN sin sincronización' },
          { value: 'multi_canal', label: 'Multi-canal (3+)' },
          { value: 'solo_local', label: 'Solo local físico' },
        ],
      },
    },
    themePresets,
    prefill,
  };
}

export default buildDashflowOnboardingConfig;
